import ExhibitionsService from 'src/services/exhibitionsService'
import LocationsService from 'src/services/locations'
import Exhibition from 'src/models/Exhibition.model'
import initialState from './state'

// Las fotos llegan como data:image (el campo de evidencias convierte el blob de la
// camara a dataURL); si alguna llega como file:// (URI nativa) se convierte aqui.
async function normalizePicture (image) {
  if (!globalThis.Capacitor) {
    return image
  }
  if (typeof image === 'string' && image.startsWith('data:image')) {
    return image
  }
  if (typeof image === 'string' && image.startsWith('file://')) {
    const { loadAndCompress } = await import('src/services/offline-images')
    const base64 = await loadAndCompress(image, 1200)
    if (typeof base64 === 'string' && base64.startsWith('data:image')) {
      return base64
    }
    return null
  }
  return image
}

async function normalizePictures (payload) {
  if (!globalThis.Capacitor) return payload
  if (!payload || !Array.isArray(payload.pictures)) return payload

  const pictures = []
  for (const image of payload.pictures) {
    const normalized = await normalizePicture(image)
    if (normalized) pictures.push(normalized)
  }
  return { ...payload, pictures }
}

// Convierte una data URL (base64) en Blob binario para enviarla via multipart.
function base64ToBlob (dataUrl) {
  const [meta, b64] = String(dataUrl).split(',')
  const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/jpeg'
  const binary = atob(b64 || '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

// Arma el FormData del payload: campos como texto y cada evidencia como archivo
// binario (pictures[]). Enviar las imagenes como multipart en vez de base64 en el
// body JSON evita exceder el limite de tamano del request con varias fotos.
function buildExhibitionFormData (payload) {
  const formData = new FormData()
  const pictures = Array.isArray(payload.pictures) ? payload.pictures : []

  Object.keys(payload).forEach(key => {
    if (key === 'pictures') return
    const value = payload[key]
    if (value !== null && value !== undefined) formData.append(key, value)
  })

  pictures.forEach((picture, index) => {
    const blob = base64ToBlob(picture)
    const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg')
    formData.append('pictures[]', blob, `evidence-${index}.${ext}`)
  })

  return formData
}

export default {
  resetFilters () {
    this.filters = initialState().filters
    this.hasActiveFilters = false
  },
  updateFilters (payload) {
    this.filters = { ...this.filters, ...payload }
  },
  // Aplica filtros desde el diálogo y marca que hay busqueda con filtros activa.
  applyFilters (payload) {
    this.filters = { ...this.filters, ...payload }
    this.hasActiveFilters = true
  },
  // Devuelve true si cargo, false si fallo (para que la pagina pueda reintentar).
  async fetchFiltered (options = {}) {
    const payload = { limit: this.pagination.rowsPerPage, page: 1, ...options }
    this.loading = true
    try {
      const { data } = await ExhibitionsService.fetchFiltered(payload)
      this.exhibitions = data.data.map(row => new Exhibition(row))
      this.pagination = {
        page: data.current_page || payload.page,
        rowsPerPage: payload.limit,
        rowsNumber: data.total || 0
      }
      return true
    } catch (error) {
      console.error(error)
      return false
    } finally {
      this.loading = false
    }
  },
  async findExhibitionById (id) {
    // Limpia la exhibicion previa para no mostrar datos/botones stale mientras carga
    // (evita el parpadeo de "agregar evidencia" / "finalizar" al entrar al detalle).
    this.exhibition = null
    try {
      const { data } = await ExhibitionsService.getById(id)
      this.exhibition = new Exhibition(data)
    } catch (error) {
      console.error(error)
    }
  },
  async createExhibition (payload) {
    const normalized = await normalizePictures(payload)
    const { data } = await ExhibitionsService.create(buildExhibitionFormData(normalized))
    return data
  },
  async finalizeExhibition (payload) {
    const normalized = await normalizePictures(payload)
    const { data } = await ExhibitionsService.changeStatus(buildExhibitionFormData(normalized))
    return data
  },
  async addEvidence (payload) {
    const normalized = await normalizePictures(payload)
    const { data } = await ExhibitionsService.addEvidence(buildExhibitionFormData(normalized))
    return data
  },
  async removeEvidence (evidenceId) {
    const { data } = await ExhibitionsService.deleteEvidence(evidenceId)
    return data
  },
  async fetchTypes () {
    try {
      const { data } = await ExhibitionsService.fetchTypes()
      this.types = data
    } catch (error) {
      console.error(error)
    }
  },
  async fetchPlaces () {
    try {
      const { data } = await ExhibitionsService.fetchPlaces()
      this.places = data
    } catch (error) {
      console.error(error)
    }
  },
  async fetchPromoters () {
    try {
      // limit alto: el filtro Responsable lista los subordinados sin buscador.
      // El backend ya acota por permiso + jerarquia + organizacion, y pagina la respuesta.
      const { data } = await ExhibitionsService.fetchPromoters({ limit: 200 })
      this.promoters = data.data
    } catch (error) {
      console.error(error)
    }
  },
  async searchProducts (options) {
    const { data } = await ExhibitionsService.fetchProducts(options)
    return data
  },
  async searchPromoters (options) {
    const { data } = await ExhibitionsService.fetchPromoters(options)
    return data
  },
  // Ubicaciones para el filtro (busqueda por valor si hay termino, primera pagina si no).
  async searchLocations (value) {
    const raw = (value && value.length >= 2)
      ? await LocationsService.fetchByValue({ search: value })
      : await LocationsService.fetchAll()
    return Array.isArray(raw) ? raw : (raw.data || [])
  },

  // --- Admin de catalogos (productos / tipos / lugares) ---
  async fetchCatalogProducts (options) {
    const { data } = await ExhibitionsService.fetchProducts(options)
    return data
  },
  async fetchCatalogTypes () {
    const { data } = await ExhibitionsService.fetchTypes()
    return data
  },
  async fetchCatalogPlaces () {
    const { data } = await ExhibitionsService.fetchPlaces()
    return data
  },
  createProduct (payload) {
    return ExhibitionsService.createProduct(payload)
  },
  updateProduct (id, payload) {
    return ExhibitionsService.updateProduct(id, payload)
  },
  deleteProduct (id) {
    return ExhibitionsService.deleteProduct(id)
  },
  importCatalogProducts (formData) {
    return ExhibitionsService.importProducts(formData)
  },
  createType (payload) {
    return ExhibitionsService.createType(payload)
  },
  updateType (id, payload) {
    return ExhibitionsService.updateType(id, payload)
  },
  deleteType (id) {
    return ExhibitionsService.deleteType(id)
  },
  createPlace (payload) {
    return ExhibitionsService.createPlace(payload)
  },
  updatePlace (id, payload) {
    return ExhibitionsService.updatePlace(id, payload)
  },
  deletePlace (id) {
    return ExhibitionsService.deletePlace(id)
  }
}
